import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { gql } from "@apollo/client";
import type { TypedDocumentNode } from "@graphql-typed-document-node/core";
import { useMutation, useQuery } from "@apollo/client/react";
import {
  Button,
  Card,
  Group,
  MantineColor,
  SimpleGrid,
  Stack,
  Stepper,
  Tabs,
  Text,
  Title,
  Tooltip,
} from "@mantine/core";
import {
  IconArrowsSort,
  IconChartBar,
  IconNotebook,
  IconRefresh,
  IconUsers,
} from "@tabler/icons-react";
import { modals } from "@mantine/modals";
import { notifications } from "@mantine/notifications";
import {
  GroupConfiguration,
  Maybe,
  PageInfo,
  Player,
  ResponsePageInfo,
  StepStatus,
  StepType,
  TournamentStep,
  TournamentTeam,
  TournamentTeamMemberDto,
  TournamentTeamStatus,
} from "../../../../../gql/graphql";
import { useTeamsFromTournament } from "../../../../../business/teams.hook";
import { usePaginatedLoadAll } from "../../../../../business/paginated-query.hook";
import {
  GenerateStepModal,
  SelectTeamsModal,
  UpdateScoreModal,
  createDefaultScore,
} from "../TournamentStepModals";
import { StatusBadge } from "../../../components/StatusBadge";
import { DetailPageSkeleton } from "../../../components/SkeletonLoader";
import { SingleElimBracket } from "./views/SingleElimBracket";
import { GroupStageView } from "./views/GroupStageView";
import type { BracketGroup, TeamScore } from "./views/bracket-utils";
import {
  BUILTIN_PRESETS_QUERY,
  STEP_RULE_SET_STRUCTURE_QUERY,
} from "./rules/graphql/queries";
import { graphql } from "gql.tada";
import { SeedingPanel } from "../seeding/SeedingPanel";
import type { SeedingPinState } from "../seeding/SeedingGroupPinTab";
import { RESEED_STEP_MUTATION } from "../seeding/seeding.operations";
import { TournamentStepJournalTab } from "../TournamentStepJournalTab";

const STEP_RULE_SET_PRESET_NAME_QUERY = graphql(`
  query StepRuleSetPresetName($tournamentStepId: ID!) {
    stepRuleSet(tournamentStepId: $tournamentStepId) {
      id
      presetName
    }
  }
`);

const CROSS_STEP_WARNING =
  "Cross-step side effects (e.g. teams already advanced to other steps) will NOT be reversed. You must handle those manually.";

const STEPPER_CFG: Record<StepStatus, number> = {
  CONFIGURED: 1,
  GENERATING: 1,
  GENERATED: 2,
  SEEDING: 2,
  SEEDED: 3,
  STARTED: 4,
  ENDED: 5,
};

type ResetActionKind = "RESTART" | "RESET_SEEDING" | "FULL_RESET";

interface ResetActionConfig {
  kind: ResetActionKind;
  buttonLabel: string;
  buttonColor: MantineColor;
  buttonVariant: "filled" | "light";
  modalTitle: (status: StepStatus) => string;
  modalBody: (status: StepStatus) => string;
  confirmLabel: (status: StepStatus) => string;
  confirmColor: (status: StepStatus) => MantineColor;
  isAvailable: (status: StepStatus) => boolean;
  showsCrossStepWarning: (status: StepStatus) => boolean;
}

const RESET_ACTIONS: ResetActionConfig[] = [
  {
    kind: "RESTART",
    buttonLabel: "Restart Step",
    buttonColor: "yellow",
    buttonVariant: "light",
    modalTitle: () => "Reset started step?",
    modalBody: () =>
      "This will wipe all scores, match results, and rule execution logs. The bracket structure and team seedings will be preserved. The step will return to SEEDED status.",
    confirmLabel: () => "Reset",
    confirmColor: () => "red",
    isAvailable: (status) => status === StepStatus.Started,
    showsCrossStepWarning: () => true,
  },
  {
    kind: "RESET_SEEDING",
    buttonLabel: "Reset Seeding",
    buttonColor: "orange",
    buttonVariant: "light",
    modalTitle: (status) =>
      status === StepStatus.Started ? "Reset started step?" : "Reset Seeding",
    modalBody: (status) =>
      status === StepStatus.Started
        ? "This will wipe all scores, match results, rule execution logs, AND team seedings. Only the bracket structure will be preserved. The step will return to GENERATED status."
        : "This will clear all team assignments and scores but keep the bracket structure. Continue?",
    confirmLabel: (status) =>
      status === StepStatus.Started ? "Reset" : "Reset Seeding",
    confirmColor: (status) =>
      status === StepStatus.Started ? "red" : "yellow",
    isAvailable: (status) =>
      status === StepStatus.Seeded || status === StepStatus.Started,
    showsCrossStepWarning: (status) => status === StepStatus.Started,
  },
  {
    kind: "FULL_RESET",
    buttonLabel: "Full Reset",
    buttonColor: "red",
    buttonVariant: "light",
    modalTitle: (status) =>
      status === StepStatus.Started ? "Reset started step?" : "Full Reset",
    modalBody: (status) =>
      status === StepStatus.Started
        ? "This will delete the entire bracket structure, all scores, match results, and rule execution logs. The step will return to CONFIGURED status."
        : "This will delete all generated brackets and reset the step to Configured. This cannot be undone. Continue?",
    confirmLabel: (status) =>
      status === StepStatus.Started ? "Reset" : "Full Reset",
    confirmColor: () => "red",
    isAvailable: (status) =>
      status === StepStatus.Generated ||
      status === StepStatus.Seeded ||
      status === StepStatus.Generating ||
      status === StepStatus.Seeding ||
      status === StepStatus.Started,
    showsCrossStepWarning: (status) => status === StepStatus.Started,
  },
];

export const StepDashboard = () => {
  const { stepId, tournamentId } = useParams();
  const navigate = useNavigate();
  const [selectedTeams, setSelectedTeams] = useState<
    (Omit<TournamentTeam, "members"> & {
      members: (TournamentTeamMemberDto & { player: Player })[];
    })[]
  >([]);

  const { loading: loadingStepShape, data: stepShape } = useQuery<
    { tournamentStepGeneratedShape: BracketGroup[] },
    { stepId: string }
  >(
    gql`
      query ($stepId: ID!) {
        tournamentStepGeneratedShape(stepId: $stepId) {
          id
          name
          rounds {
            id
            name
            order
            games {
              id
              order
              status
              losingGameId
              winningGameId
              configuration {
                wonMatchCountToWinGame
              }
              matches {
                id
                order
                status
              }
            }
          }
        }
      }
    `,
    {
      variables: {
        stepId: stepId ?? "",
      },
      skip: !stepId,
    },
  );

  const {
    loading: loadingScores,
    results: scores,
    refetch,
  } = usePaginatedLoadAll<
    {
      edges: TeamScore[];
      nodes: TeamScore[];
      pageInfo: ResponsePageInfo;
    },
    { page: Maybe<PageInfo>; stepId: string },
    TeamScore
  >(
    gql`
      query ($stepId: ID!, $page: PageInfo!) {
        tournamentStepGroupRoundGameMatchScoresGetForStep(
          stepId: $stepId
          page: $page
        ) {
          pageInfo {
            endCursor
            hasNextPage
          }
          nodes {
            teamId
            status
            score
            matchId
          }
        }
      }
    `,
    {
      variables: {
        stepId: stepId ?? "",
      },
      skip: !stepId,
    },
  );

  const { loading: teamsLoading, results: teams } = useTeamsFromTournament({
    tournamentId: tournamentId ?? "",
    status: TournamentTeamStatus.Confirmed,
  });

  const dataLoading = loadingStepShape || loadingScores || teamsLoading;

  const { data, loading } = useQuery(
    gql`
      query ($id: ID!) {
        tournamentStep(id: $id) {
          id
          name
          description
          type
          status
          order
          tournamentId
          seedingOrder
          manualPins {
            teamId
            groupId
            gameId
            slotIndex
          }
          configuration {
            groups {
              rounds {
                order
              }
            }
          }
        }
      }
    ` as TypedDocumentNode<{ tournamentStep: TournamentStep }>,
    {
      variables: {
        id: stepId,
      },
      skip: dataLoading,
    },
  );
  const { data: ruleSetData, loading: ruleSetLoading } = useQuery(
    STEP_RULE_SET_STRUCTURE_QUERY,
    {
      variables: { tournamentStepId: stepId ?? "" },
      skip: !stepId,
    },
  );
  const { data: ruleSetPresetData } = useQuery(
    STEP_RULE_SET_PRESET_NAME_QUERY,
    {
      variables: { tournamentStepId: stepId ?? "" },
      skip: !stepId,
    },
  );
  const { data: builtinPresetsData } = useQuery(BUILTIN_PRESETS_QUERY, {
    variables: {},
  });

  // Redirect to the rules page when the step has no rule set — generation is
  // blocked in that state, so route the user where they can fix it.
  useEffect(() => {
    if (ruleSetLoading) return;
    if (!tournamentId || !stepId) return;
    if (!ruleSetData) return;
    if (ruleSetData.stepRuleSet) return;
    navigate(`/tournaments/${tournamentId}/steps/${stepId}/rules`, {
      replace: true,
    });
  }, [ruleSetLoading, ruleSetData, tournamentId, stepId, navigate]);

  const scoreTeamIds = useMemo(
    () => new Set(scores.map((s) => s.teamId)),
    [scores],
  );
  const hasStepData = !!data;

  useEffect(() => {
    if (!hasStepData) return;
    setSelectedTeams(teams.filter((team) => scoreTeamIds.has(team.id)));
  }, [hasStepData, teams, scoreTeamIds]);

  const [reseedStep] = useMutation(RESEED_STEP_MUTATION);

  const [startStep] = useMutation<
    {
      startTournamentStep: boolean;
    },
    { stepId: string }
  >(gql`
    mutation ($stepId: ID!) {
      startTournamentStep(stepId: $stepId)
    }
  `);

  const [resetSeeding] = useMutation<
    { resetTournamentStepSeeding: boolean },
    { stepId: string }
  >(gql`
    mutation ($stepId: ID!) {
      resetTournamentStepSeeding(stepId: $stepId)
    }
  `);

  const [fullReset] = useMutation<
    { resetTournamentStep: boolean },
    { stepId: string }
  >(gql`
    mutation ($stepId: ID!) {
      resetTournamentStep(stepId: $stepId)
    }
  `);

  const [restartStep] = useMutation<
    { restartTournamentStep: boolean },
    { stepId: string }
  >(gql`
    mutation ($stepId: ID!) {
      restartTournamentStep(stepId: $stepId)
    }
  `);

  const resetMutationByKind = useMemo<
    Record<
      ResetActionKind,
      (options: { variables: { stepId: string } }) => Promise<{
        data?: unknown;
        error?: { message: string };
      }>
    >
  >(
    () => ({
      RESTART: restartStep,
      RESET_SEEDING: resetSeeding,
      FULL_RESET: fullReset,
    }),
    [restartStep, resetSeeding, fullReset],
  );

  const handleGameTeamClick = useCallback(
    (gameId: string, teamId: string) => {
      if (data?.tournamentStep?.status !== StepStatus.Started) return;

      const game = stepShape?.tournamentStepGeneratedShape
        ?.flatMap((g) => g.rounds.flatMap((r) => r.games))
        .find((g) => g.id === gameId);

      if (!game?.matches.length) return;

      const fullTeam = teams.find((t) => t.id === teamId);
      if (!fullTeam) return;

      const teamScores = game.matches.map((match) => {
        const existing = scores.find(
          (s) => s.teamId === teamId && s.matchId === match.id,
        );
        return existing ?? createDefaultScore(teamId, match.id);
      });

      modals.open({
        title: `Update ${fullTeam.name}'s score`,
        id: "update-score",
        centered: true,
        onClose() {
          if (!stepId) return;
          setTimeout(() => refetch({ stepId }), 0);
        },
        children: <UpdateScoreModal scores={teamScores} team={fullTeam} />,
      });
    },
    [data?.tournamentStep?.status, stepShape, stepId, refetch, teams, scores],
  );

  const executeMutationAndRefetch = useCallback(
    <TData,>(
      mutationFn: (options: { variables: { stepId: string } }) => Promise<{
        data?: TData | null;
        error?: { message: string };
      }>,
    ) => {
      if (!stepId) return;
      mutationFn({ variables: { stepId } })
        .then((result) => {
          if (result?.error) {
            throw new Error(result.error.message);
          }
          refetch({ stepId });
          modals.closeAll();
        })
        .catch((err: unknown) => {
          notifications.show({
            title: "Action failed",
            message: err instanceof Error ? err.message : "Unexpected error",
            color: "red",
          });
        });
    },
    [stepId, refetch],
  );

  if (dataLoading || loading) return <DetailPageSkeleton />;

  const stepType = data?.tournamentStep?.type;
  const stepStatus = data?.tournamentStep?.status;
  const ruleSetValidated: boolean =
    ruleSetData?.stepRuleSet?.validated === true;
  const canGenerate: boolean =
    stepStatus === StepStatus.Configured && ruleSetValidated;
  const seedingOrder: readonly string[] =
    data?.tournamentStep?.seedingOrder ?? [];

  // Initial pin state for the SeedingPanel tabs is sourced from the step's
  // persisted `manualPins` — backend narrows the raw JsonValue to
  // `[SeedingPin!]!` via its resolver, so an empty list is the canonical
  // "no pins" value (never null/undefined).
  const initialPins: readonly SeedingPinState[] =
    data?.tournamentStep?.manualPins?.map((pin) => ({
      teamId: pin.teamId,
      groupId: pin.groupId ?? null,
      gameId: pin.gameId ?? null,
      slotIndex: pin.slotIndex ?? null,
    })) ?? [];

  // Correlate the step's rule set with a built-in preset by displayName so we
  // can read the pinning-support flags. When the rule set was authored from a
  // user preset script we gracefully fall back to order-only (no pinning).
  // TODO(seeding-rules): move to a stable preset slug / builtinPresetId on
  // StepRuleSet once a dedicated column exists — the displayName match is
  // brittle against future preset renames.
  const rulesetPresetName = ruleSetPresetData?.stepRuleSet?.presetName ?? null;
  const matchingBuiltinPreset = rulesetPresetName
    ? (builtinPresetsData?.builtinPresets ?? []).find(
        (p) => p.displayName === rulesetPresetName,
      )
    : undefined;
  const presetPinningSupport = {
    supportsPinning: matchingBuiltinPreset?.supportsPinning === true,
    supportsGroupPinning: matchingBuiltinPreset?.supportsGroupPinning ?? null,
    supportsSlotPinning: matchingBuiltinPreset?.supportsSlotPinning ?? null,
  };

  const seedingGroups = (stepShape?.tournamentStepGeneratedShape ?? []).map(
    (g) => ({ id: g.id, name: g.name }),
  );
  const seedingEntrySlots = (
    stepShape?.tournamentStepGeneratedShape ?? []
  ).flatMap((group) => {
    // Entry slots are those in the first round of each group (the round
    // where new teams are placed).
    const sortedRounds = [...group.rounds].sort((a, b) => a.order - b.order);
    const entryRound = sortedRounds[0];
    if (!entryRound) return [];
    const entrySlots: {
      gameId: string;
      gameLabel: string;
      slotIndex: number;
      groupName: string;
      roundName: string;
    }[] = [];
    const sortedGames = [...entryRound.games].sort(
      (a, b) => a.order - b.order,
    );
    // Each entry-round game has exactly two seed slots (the two teams that
    // face off in the first round of a group). Keep this literal so the
    // intent is obvious — if we ever support non-binary entry games the
    // value will come from `game.configuration`.
    const ENTRY_SLOTS_PER_GAME = 2;
    for (const game of sortedGames) {
      for (
        let slotIndex = 0;
        slotIndex < ENTRY_SLOTS_PER_GAME;
        slotIndex++
      ) {
        entrySlots.push({
          gameId: game.id,
          gameLabel: `Game ${game.order + 1}`,
          slotIndex,
          groupName: group.name,
          roundName: entryRound.name,
        });
      }
    }
    return entrySlots;
  });

  const showSeedingPanel =
    stepStatus === StepStatus.Generated || stepStatus === StepStatus.Seeded;

  const renderBracketView = () => {
    const groups = stepShape?.tournamentStepGeneratedShape ?? [];
    const teamList = teams.map((t) => ({ id: t.id, name: t.name }));

    if (stepType === StepType.SingleElim || stepType === StepType.DoubleElim) {
      return (
        <SingleElimBracket
          groups={groups}
          teams={teamList}
          scores={scores}
          stepStatus={stepStatus}
          onGameTeamClick={handleGameTeamClick}
        />
      );
    }

    // RoundRobin, Score, Custom — all use the group stage view
    return (
      <GroupStageView
        groups={groups}
        teams={teamList}
        scores={scores}
        stepStatus={stepStatus}
        onGameTeamClick={handleGameTeamClick}
      />
    );
  };

  return (
    <div className="wp-fade-in">
      <Tabs defaultValue="overview" keepMounted={false}>
        <Tabs.List mb="md">
          <Tabs.Tab value="overview" leftSection={<IconChartBar size={16} />}>
            Overview
          </Tabs.Tab>
          <Tabs.Tab value="journal" leftSection={<IconNotebook size={16} />}>
            Journal
          </Tabs.Tab>
        </Tabs.List>

        <Tabs.Panel value="overview">
          <Stack>
            <Stack>
              <Title>Dashboard</Title>
              <Stepper active={STEPPER_CFG[stepStatus ?? StepStatus.Configured]}>
                <Stepper.Step label="Configured" />
                <Stepper.Step
                  label="Generated"
                  loading={stepStatus === StepStatus.Generating}
                />
                <Stepper.Step
                  label="Seeded"
                  loading={stepStatus === StepStatus.Seeding}
                />
                <Stepper.Step label="Started" />
              </Stepper>
            </Stack>

            <SimpleGrid cols={2}>
          {/* Step Info Card */}
          <Card pt={0}>
            <Card.Section>
              <Group justify="space-between" p={10}>
                <Group gap={10}>
                  <IconChartBar />
                  <Text size="md" fw={700}>
                    {data?.tournamentStep?.name}
                  </Text>
                </Group>
                <StatusBadge status={stepStatus ?? StepStatus.Configured} />
              </Group>
            </Card.Section>
            <Stack>
              {data?.tournamentStep?.description}
              {stepStatus === StepStatus.Seeded && (
                <Button
                  color="green"
                  className="wp-btn-press"
                  onClick={() => {
                    modals.openConfirmModal({
                      title: "Start step",
                      id: "start-step",
                      children: (
                        <Text>
                          Are you sure you want to start this step? You can
                          reset it later, but cross-step side effects will not
                          be reversed automatically.
                        </Text>
                      ),
                      confirmProps: { color: "green" },
                      labels: {
                        cancel: "Cancel",
                        confirm: "Start",
                      },
                      centered: true,
                      onConfirm: () => executeMutationAndRefetch(startStep),
                    });
                  }}
                >
                  Start step
                </Button>
              )}
              {stepStatus === StepStatus.Seeded && (
                <Button
                  color="cyan"
                  variant="light"
                  className="wp-btn-press"
                  leftSection={<IconRefresh size={16} />}
                  onClick={() => {
                    modals.openConfirmModal({
                      title: "Re-seed step",
                      id: "reseed-step",
                      centered: true,
                      children: (
                        <Stack gap="sm">
                          <Text>
                            This will re-run the seeding pipeline from the
                            step&apos;s currently persisted seedingOrder and
                            manual pins. The step will briefly return to
                            GENERATED status, then back to SEEDED.
                          </Text>
                          <Text c="orange" size="sm" fw={500}>
                            Any unsaved match results would be lost — only safe
                            before the step has STARTED.
                          </Text>
                        </Stack>
                      ),
                      labels: { confirm: "Re-seed", cancel: "Cancel" },
                      confirmProps: { color: "cyan" },
                      onConfirm: () => executeMutationAndRefetch(reseedStep),
                    });
                  }}
                >
                  Re-seed step
                </Button>
              )}
              {stepStatus &&
                RESET_ACTIONS.filter((action) =>
                  action.isAvailable(stepStatus),
                ).map((action) => {
                  const mutationFn = resetMutationByKind[action.kind];
                  return (
                    <Button
                      key={action.kind}
                      color={action.buttonColor}
                      variant={action.buttonVariant}
                      className="wp-btn-press"
                      onClick={() => {
                        modals.openConfirmModal({
                          title: action.modalTitle(stepStatus),
                          id: `reset-action-${action.kind.toLowerCase()}`,
                          children: (
                            <Stack gap="sm">
                              <Text>{action.modalBody(stepStatus)}</Text>
                              {action.showsCrossStepWarning(stepStatus) && (
                                <Text c="orange" size="sm" fw={500}>
                                  {CROSS_STEP_WARNING}
                                </Text>
                              )}
                            </Stack>
                          ),
                          confirmProps: {
                            color: action.confirmColor(stepStatus),
                          },
                          labels: {
                            cancel: "Cancel",
                            confirm: action.confirmLabel(stepStatus),
                          },
                          centered: true,
                          onConfirm: () =>
                            executeMutationAndRefetch(mutationFn),
                        });
                      }}
                    >
                      {action.buttonLabel}
                    </Button>
                  );
                })}
            </Stack>
          </Card>

          {/* Configuration Card */}
          <Card pt={0}>
            <Card.Section>
              <Group justify="space-between" p={10}>
                <Group gap={10}>
                  <IconChartBar />
                  <Text size="md" fw={700}>
                    Configuration
                  </Text>
                </Group>
                <Tooltip
                  label="Rule set must be validated before generation"
                  disabled={canGenerate || stepStatus !== StepStatus.Configured}
                >
                  <span>
                    <Button
                      className="wp-btn-press"
                      disabled={!canGenerate}
                      onClick={() => {
                        const hasRuleSetStructure =
                          ruleSetData?.stepRuleSet?.structureTemplate?.groupTemplates?.some(
                            (g) => g.roundTemplates?.length,
                          );
                        if (
                          !hasRuleSetStructure &&
                          (!data?.tournamentStep?.configuration?.groups
                            ?.length ||
                            data?.tournamentStep?.configuration?.groups?.some(
                              (group: GroupConfiguration) =>
                                !group.rounds?.length,
                            ))
                        ) {
                          modals.open({
                            title: "Error",
                            id: "error",
                            centered: true,
                            children: (
                              <Stack>
                                <Text>
                                  You cannot generate a step that has no groups
                                  or groups with no rounds.
                                </Text>
                                <Text>
                                  Please add at least one group with at least
                                  one round.
                                </Text>
                              </Stack>
                            ),
                          });
                          return;
                        }
                        const tournamentStepId = data?.tournamentStep?.id;
                        const tournamentStepTournamentId =
                          data?.tournamentStep?.tournamentId;
                        if (!tournamentStepId || !tournamentStepTournamentId) {
                          return;
                        }
                        modals.open({
                          title: "Generate step",
                          id: "generate-step",
                          centered: true,
                          onClose() {
                            if (!stepId) return;
                            setTimeout(() => refetch({ stepId }), 0);
                          },
                          children: (
                            <GenerateStepModal
                              tournamentId={tournamentStepTournamentId}
                              stepId={tournamentStepId}
                            />
                          ),
                        });
                      }}
                    >
                      Generate
                    </Button>
                  </span>
                </Tooltip>
              </Group>
            </Card.Section>
            <SimpleGrid cols={2}>
              <Stack gap={0}>
                <Text fw={700}>Type</Text>
                <Text>{data?.tournamentStep?.type}</Text>
              </Stack>
              <Stack gap={0}>
                <Text fw={700}>Order</Text>
                <Text>{data?.tournamentStep?.order}</Text>
              </Stack>
            </SimpleGrid>
          </Card>
        </SimpleGrid>

        {/* Seeding Panel */}
        {showSeedingPanel && stepId && (
          <Card pt={0}>
            <Card.Section>
              <Group justify="space-between" p={10}>
                <Group gap={10}>
                  <IconArrowsSort />
                  <Text size="md" fw={700}>
                    Seeding
                  </Text>
                </Group>
              </Group>
            </Card.Section>
            <SeedingPanel
              stepId={stepId}
              preset={presetPinningSupport}
              teams={teams.map((t) => ({ id: t.id, name: t.name }))}
              groups={seedingGroups}
              entrySlots={seedingEntrySlots}
              seedingOrder={seedingOrder}
              initialPins={initialPins}
              onOrderSaved={() => refetch({ stepId })}
              onPinsSaved={() => refetch({ stepId })}
              onTeamWithdrawn={() => refetch({ stepId })}
              canWithdraw={stepStatus === StepStatus.Seeded}
            />
          </Card>
        )}

        {/* Bracket / Group Stage View */}
        {stepStatus !== StepStatus.Configured && (
          <Card pt={0}>
            <Card.Section>
              <Group justify="space-between" p={10}>
                <Group gap={10}>
                  <IconUsers />
                  <Text size="md" fw={700}>
                    Brackets
                  </Text>
                </Group>
                <Button
                  className="wp-btn-press"
                  disabled={
                    stepStatus === StepStatus.Started ||
                    stepStatus === StepStatus.Ended
                  }
                  onClick={() => {
                    if (
                      stepStatus !== StepStatus.Generated &&
                      stepStatus !== StepStatus.Seeded
                    ) {
                      modals.open({
                        title: "Error",
                        id: "error",
                        centered: true,
                        children: (
                          <Stack>
                            <Text>
                              You cannot seed a step that has not been
                              generated.
                            </Text>
                            <Text>
                              Please generate the step before seeding it.
                            </Text>
                          </Stack>
                        ),
                      });
                      return;
                    }
                    if (!tournamentId || !stepId) return;
                    modals.open({
                      title: "Seed step",
                      id: "seed-step",
                      centered: true,
                      size: "xl",
                      children: (
                        <SelectTeamsModal
                          tournamentId={tournamentId}
                          stepId={stepId}
                          onSeeded={() => {
                            if (!stepId) return;
                            refetch({ stepId });
                          }}
                        />
                      ),
                    });
                  }}
                >
                  Select teams
                </Button>
              </Group>
            </Card.Section>
            {stepType && renderBracketView()}
          </Card>
        )}
          </Stack>
        </Tabs.Panel>

        <Tabs.Panel value="journal">
          {stepId && <TournamentStepJournalTab stepId={stepId} />}
        </Tabs.Panel>
      </Tabs>
    </div>
  );
};
